import { useMemo } from 'react'

import { useBoolean, useRequest } from 'ahooks'
import { Descriptions, DescriptionsProps, Spin } from 'antd'
import { createStyles } from 'antd-style'

import { downloadFileApi } from '@eastcoal/api/nexus/oss/downloadFile'
import { downloadFileBatchApi } from '@eastcoal/api/nexus/oss/downloadFileBatch'
import { queryDealNoticeApi } from '@eastcoal/api/trade/dealNotice/queryDealNotice'
import { DealNoticeEntity } from '@eastcoal/api/trade/interface'

import { DraggableModal } from '@eastcoal/common/components/DraggableModal'
import { useRemoteTableStore } from '@eastcoal/common/components/RemoteTable/Store/hooks/useStore'
import { useFeedback } from '@eastcoal/common/hooks/useFeedback'
import { downloadContent } from '@eastcoal/common/utils/filesaver'
import { pick } from '@eastcoal/common/utils/object'

import { useAuctionListStore } from '../../../Store/hooks/useStore'

const useStyles = createStyles(({ token }) => ({
  list: {
    padding: 0,
    margin: 0
  }
}))

const List = ({ data = [] }: { data?: DealNoticeEntity[] }) => {
  const { styles } = useStyles()

  return (
    <ul className={styles.list}>
      {data.map(item => (
        <li key={item.bizId}>{item.title}</li>
      ))}
    </ul>
  )
}

export const DealNoticeBatchDownloadModal = () => {
  const { message } = useFeedback()
  const { selectedRowKeys, setSelectedRows } = useRemoteTableStore(state =>
    pick(state, ['selectedRowKeys', 'setSelectedRows'])
  )
  const {
    dealNoticeBatchDownloadModalVisible,
    toggleDealNoticeBatchDownloadModalVisible
  } = useAuctionListStore(state =>
    pick(state, [
      'dealNoticeBatchDownloadModalVisible',
      'toggleDealNoticeBatchDownloadModalVisible'
    ])
  )

  const {
    loading,
    data,
    mutate: setData
  } = useRequest(
    () => queryDealNoticeApi({ bizIds: selectedRowKeys as string[] }),
    {
      manual: false,
      ready: !!selectedRowKeys.length && dealNoticeBatchDownloadModalVisible
    }
  )

  const hadNoticeEntities = useMemo(
    () => (data || []).filter(item => item.dealNoticeOssId),
    [data]
  )
  const noneNoticeEntities = useMemo(
    () => (data || []).filter(item => !item.dealNoticeOssId),
    [data]
  )

  const descriptionsItems: DescriptionsProps['items'] = [
    {
      label: '已完成',
      children: <List data={hadNoticeEntities} />
    },
    {
      label: '未完成',
      children: <List data={noneNoticeEntities} />
    }
  ]

  const handleCancel = () => {
    toggleDealNoticeBatchDownloadModalVisible(false)
  }

  const [downloading, downloadingActions] = useBoolean(false)
  const handleDownload = async () => {
    if (!hadNoticeEntities.length) {
      message.warning('当前没有成交通知书可下载！')
      return
    }
    try {
      downloadingActions.setTrue()
      if (hadNoticeEntities.length === 1) {
        await downloadSignleFile()
      } else {
        await downloadMultipleFiles()
      }
    } finally {
      downloadingActions.setFalse()
    }
    toggleDealNoticeBatchDownloadModalVisible(false)
    resetState()
  }

  const downloadSignleFile = async () => {
    const noticeEntity = hadNoticeEntities[0]
    const res = await downloadFileApi({
      ossId: noticeEntity.dealNoticeOssId
    })
    downloadContent(res, `成交通知书-${noticeEntity.title}.pdf`)
  }

  const downloadMultipleFiles = async () => {
    const filename = `成交通知书${Date.now()}.zip`
    const res = await downloadFileBatchApi({
      filename,
      ossIds: hadNoticeEntities.map(item => `${item.dealNoticeOssId}`)
    })
    downloadContent(res, filename)
  }

  const resetState = () => {
    setData(undefined)
    setSelectedRows([])
  }

  return (
    <DraggableModal
      okText='批量下载'
      open={dealNoticeBatchDownloadModalVisible}
      title='成交通知书批量下载'
      width={800}
      okButtonProps={{
        loading: downloading
      }}
      onCancel={handleCancel}
      onOk={handleDownload}
    >
      <Spin spinning={loading}>
        <Descriptions bordered column={1} items={descriptionsItems} />
      </Spin>
    </DraggableModal>
  )
}
